import { useQuery, useSubscription } from '@apollo/client';
import * as React from 'react';

import {
  MEETINGS_ACTIVE_SCHEDULE_MEETS_QUERY,
  MEETINGS_LIVE_QUICK_CALLS_QUERY,
  MEETINGS_LIVE_QUICK_MEETS_QUERY,
  MEETINGS_QUICK_CALL_EVENTS_SUBSCRIPTION,
  MEETINGS_QUICK_MEET_EVENTS_SUBSCRIPTION,
} from '../graphql/meetingsLiveIndicators';
import { isScheduleMeetRemainingToday } from '../utils/isScheduleMeetRemainingToday';

const ACTIVE_MEETS_LIMIT = 100;

type ActiveScheduleMeet = {
  id: string;
  status: string;
  startAt: string;
  nextSessionStartAt?: string | null;
  durationMinutes?: number | null;
};

export type MeetingsLiveIndicators = {
  /** Any Quick Call currently LIVE */
  hasLiveQuickCall: boolean;
  /** Any Schedule Meet session currently LIVE */
  hasLiveQuickMeet: boolean;
  /** ACTIVE Schedule Meet with a remaining occurrence today */
  hasRemainingScheduleMeetToday: boolean;
  /** Show green "LIVE" beside Meetings heading */
  showLiveLabel: boolean;
  /** Green dot beside Quick Call tab */
  showQuickCallDot: boolean;
  /** Green dot beside Schedule Meet tab (and Meetings when remaining today) */
  showScheduleMeetDot: boolean;
  /** Green dot on Meetings nav / heading for live or remaining-today */
  showMeetingsDot: boolean;
};

/**
 * Shared live indicators for admin + member Meetings pages and nav.
 * Refetches on existing quickCallEvents / quickMeetEvents sockets.
 */
export const useMeetingsLiveIndicators = (options?: {
  audienceOnly?: boolean;
  skip?: boolean;
}): MeetingsLiveIndicators => {
  const audienceOnly = options?.audienceOnly ?? false;
  const skip = options?.skip ?? false;

  const liveQuickCallsQuery = useQuery<{
    listQuickCalls: { results: Array<{ id: string }> };
  }>(MEETINGS_LIVE_QUICK_CALLS_QUERY, {
    variables: { audienceOnly, limit: 1 },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const liveQuickMeetsQuery = useQuery<{
    listLiveQuickMeets: Array<{ id: string }>;
  }>(MEETINGS_LIVE_QUICK_MEETS_QUERY, {
    variables: { audienceOnly },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const activeScheduleMeetsQuery = useQuery<{
    listQuickMeets: { results: ActiveScheduleMeet[] };
  }>(MEETINGS_ACTIVE_SCHEDULE_MEETS_QUERY, {
    variables: { audienceOnly, limit: ACTIVE_MEETS_LIMIT },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  useSubscription(MEETINGS_QUICK_CALL_EVENTS_SUBSCRIPTION, {
    variables: { audienceOnly },
    skip,
    onSubscriptionData: () => {
      void liveQuickCallsQuery.refetch();
    },
  });

  useSubscription(MEETINGS_QUICK_MEET_EVENTS_SUBSCRIPTION, {
    variables: { audienceOnly },
    skip,
    onSubscriptionData: () => {
      void liveQuickMeetsQuery.refetch();
      void activeScheduleMeetsQuery.refetch();
    },
  });

  // Re-evaluate "remaining today" as the clock moves (no refresh required).
  const [nowTick, setNowTick] = React.useState(0);
  React.useEffect(() => {
    if (skip || typeof window === 'undefined') return undefined;
    const intervalId = window.setInterval(() => {
      setNowTick((tick) => tick + 1);
    }, 30_000);
    return () => window.clearInterval(intervalId);
  }, [skip]);

  const hasLiveQuickCall = Boolean(
    liveQuickCallsQuery.data?.listQuickCalls?.results.length
  );
  const hasLiveQuickMeet = Boolean(
    liveQuickMeetsQuery.data?.listLiveQuickMeets.length
  );
  const hasRemainingScheduleMeetToday = React.useMemo(() => {
    const results = activeScheduleMeetsQuery.data?.listQuickMeets?.results || [];
    return results.some((meet) => isScheduleMeetRemainingToday(meet));
  }, [activeScheduleMeetsQuery.data?.listQuickMeets?.results, nowTick]);

  const showLiveLabel = hasLiveQuickCall || hasLiveQuickMeet;
  const showQuickCallDot = hasLiveQuickCall;
  const showScheduleMeetDot = hasRemainingScheduleMeetToday || hasLiveQuickMeet;
  const showMeetingsDot = showLiveLabel || showScheduleMeetDot;

  return {
    hasLiveQuickCall,
    hasLiveQuickMeet,
    hasRemainingScheduleMeetToday,
    showLiveLabel,
    showQuickCallDot,
    showScheduleMeetDot,
    showMeetingsDot,
  };
};
